import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Button,
  Card,
  Chip,
  PageHeaderLayout,
  TextField,
  Typography,
} from '@/ui';

import type { ChangeEvent } from 'react';

import {
  useAttachPulseAdminReportSourceMutation,
  useGetPulseAdminReportQuery,
  useRebuildPulseAdminReportMutation,
  useUpdatePulseAdminReportOverridesMutation,
  useUpdatePulseAdminReportStatusMutation,
} from '@/store/api/pulseAdminApi';

const pulseReportLoadMessage = (status: string | null) => {
  if (status === '403') return 'Access denied while loading this pulse report.';
  if (status === '404') return 'This pulse report could not be found.';

  return 'This pulse report could not be loaded.';
};

const PulseReportDetailPage = () => {
  const { reportId = '' } = useParams();
  const [notice, setNotice] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [attachSourceId, setAttachSourceId] = useState('');
  const {
    data: report,
    error,
    isError,
    isLoading,
  } = useGetPulseAdminReportQuery(reportId);
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdatePulseAdminReportStatusMutation();
  const [updateOverrides, { isLoading: isUpdatingOverrides }] =
    useUpdatePulseAdminReportOverridesMutation();
  const [attachSource, { isLoading: isAttachingSource }] =
    useAttachPulseAdminReportSourceMutation();
  const [rebuildReport, { isLoading: isRebuilding }] =
    useRebuildPulseAdminReportMutation();

  const [hiddenSections, setHiddenSections] = useState<string[]>([]);
  const [hiddenQuestions, setHiddenQuestions] = useState<string[]>([]);
  const [isOverridesDirty, setIsOverridesDirty] = useState(false);
  const hydratedReportIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!report) return;

    const reportChanged = hydratedReportIdRef.current !== report.id;
    hydratedReportIdRef.current = report.id;

    if (reportChanged || !isOverridesDirty) {
      setHiddenSections(report.overrides.hiddenSections ?? []);
      setHiddenQuestions(report.overrides.hiddenQuestions ?? []);
      setIsOverridesDirty(false);
    }
  }, [isOverridesDirty, report]);

  const sectionOptions = report?.overrideOptions.sections ?? [];
  const questionOptions = report?.overrideOptions.questions ?? [];

  const visibleSourceRows = useMemo(() => report?.sources ?? [], [report]);

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-screen-xl p-4">
        <Typography variant="body1">Loading pulse report…</Typography>
      </div>
    );
  }

  if (isError || !report) {
    const queryError: unknown = error;
    const status =
      typeof queryError === 'object' &&
      queryError !== null &&
      'status' in queryError
        ? String(queryError.status)
        : null;
    const message = pulseReportLoadMessage(status);

    return (
      <div className="container mx-auto max-w-screen-xl p-4">
        <PageHeaderLayout title="Pulse Report Workflow">
          <Link
            className="no-underline"
            to="/coach/manage/typeform?tab=pulseReports"
          >
            <Button variant="outlined">Back to pulse reports</Button>
          </Link>
        </PageHeaderLayout>
        <Card className="mt-6 p-4">
          <Typography variant="body1">{message}</Typography>
        </Card>
      </div>
    );
  }

  const toggleValue = (
    current: string[],
    value: string,
    setter: (next: string[]) => void,
  ) => {
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];

    setter(next.sort());
  };

  const handleStatusToggle = async () => {
    try {
      setErrorMessage('');
      await updateStatus({
        id: report.id,
        status: report.status === 'hidden' ? 'active' : 'hidden',
      }).unwrap();
      setNotice(
        report.status === 'hidden'
          ? 'Pulse report restored.'
          : 'Pulse report hidden.',
      );
    } catch (caughtError) {
      console.error(caughtError);
      setErrorMessage('Could not update pulse report visibility.');
    }
  };

  const handleSaveOverrides = async () => {
    try {
      setErrorMessage('');
      await updateOverrides({
        id: report.id,
        overrides: {
          hiddenQuestions,
          hiddenSections,
        },
      }).unwrap();
      setIsOverridesDirty(false);
      setNotice('Pulse report overrides updated.');
    } catch (caughtError) {
      console.error(caughtError);
      setErrorMessage('Could not update pulse report overrides.');
    }
  };

  const handleAttachSource = async () => {
    try {
      if (!attachSourceId.trim()) {
        setErrorMessage('Enter a raw submission ID before attaching.');
        return;
      }

      setErrorMessage('');
      await attachSource({
        id: report.id,
        formResponseId: attachSourceId.trim(),
      }).unwrap();
      setAttachSourceId('');
      setNotice('Correction source attached.');
    } catch (caughtError) {
      console.error(caughtError);
      setErrorMessage('Could not attach this correction source.');
    }
  };

  const handleRebuild = async () => {
    try {
      setErrorMessage('');
      await rebuildReport(report.id).unwrap();
      setNotice('Pulse report rebuilt.');
    } catch (caughtError) {
      console.error(caughtError);
      setErrorMessage('Could not rebuild this pulse report.');
    }
  };

  return (
    <div className="container mx-auto max-w-screen-xl p-4">
      <PageHeaderLayout
        subtitle={report.memberEmail ?? 'No member email'}
        title="Pulse Report Workflow"
      >
        <Link
          className="no-underline"
          to="/coach/manage/typeform?tab=pulseReports"
        >
          <Button variant="outlined">Back to pulse reports</Button>
        </Link>
      </PageHeaderLayout>

      {notice ? (
        <div className="mt-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {notice}
        </div>
      ) : null}
      {errorMessage ? (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Typography variant="h5">{report.memberName}</Typography>
            <Chip
              color={report.status === 'hidden' ? 'warning' : 'success'}
              label={report.status}
            />
            <Chip color="default" label={report.formType} />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Typography variant="subtitle2">Submitted</Typography>
              <Typography variant="body2">
                {new Date(report.submittedAt).toLocaleString()}
              </Typography>
            </div>
            <div>
              <Typography variant="subtitle2">Source version</Typography>
              <Typography variant="body2">
                {report.sourceVersion ?? 'Unknown'}
              </Typography>
            </div>
            <div>
              <Typography variant="subtitle2">Sources</Typography>
              <Typography variant="body2">{report.sourceCount}</Typography>
            </div>
            <div>
              <Typography variant="subtitle2">Debrief</Typography>
              <Typography variant="body2">
                {report.debrief?.debriefedByName
                  ? `${report.debrief.debriefedByName} at ${new Date(report.debrief.debriefedAt ?? '').toLocaleString()}`
                  : 'Not debriefed'}
              </Typography>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button disabled={isUpdatingStatus} onClick={handleStatusToggle}>
              {report.status === 'hidden' ? 'Restore' : 'Hide'}
            </Button>
            <Button
              disabled={isRebuilding}
              variant="outlined"
              onClick={handleRebuild}
            >
              Rebuild
            </Button>
          </div>
        </Card>

        <Card className="p-4">
          <Typography variant="h5">Attach correction source</Typography>
          <Typography className="mt-2" variant="body2">
            Attach another raw submission by ID. The source must already be
            active, pulse-eligible, linked to this member, and unattached.
          </Typography>
          <div className="mt-4 flex flex-col gap-3">
            <TextField
              label="Raw submission ID"
              value={attachSourceId}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setAttachSourceId(event.target.value);
              }}
            />
            <Button disabled={isAttachingSource} onClick={handleAttachSource}>
              Attach source
            </Button>
          </div>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="p-4">
          <Typography variant="h5">Suppression overrides</Typography>
          <Typography className="mt-2" variant="body2">
            Hide sections or individual questions from the projected report and
            aggregate reads.
          </Typography>

          <div className="mt-4">
            <Typography variant="subtitle2">Sections</Typography>
            <div className="mt-2 flex flex-col gap-2">
              {sectionOptions.map((section) => (
                <label
                  key={section.key}
                  className="flex items-center gap-2 text-sm"
                  htmlFor={`section-override-${section.key}`}
                >
                  <input
                    checked={hiddenSections.includes(section.key)}
                    id={`section-override-${section.key}`}
                    type="checkbox"
                    onChange={() => {
                      setIsOverridesDirty(true);
                      toggleValue(
                        hiddenSections,
                        section.key,
                        setHiddenSections,
                      );
                    }}
                  />
                  <span>{section.title}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <Typography variant="subtitle2">Questions</Typography>
            <div className="mt-2 flex flex-col gap-2">
              {questionOptions.map((question) => (
                <label
                  key={question.key}
                  className="flex items-center gap-2 text-sm"
                  htmlFor={`question-override-${question.key}`}
                >
                  <input
                    checked={hiddenQuestions.includes(question.key)}
                    id={`question-override-${question.key}`}
                    type="checkbox"
                    onChange={() => {
                      setIsOverridesDirty(true);
                      toggleValue(
                        hiddenQuestions,
                        question.key,
                        setHiddenQuestions,
                      );
                    }}
                  />
                  <span>{question.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <Button
              disabled={isUpdatingOverrides}
              onClick={handleSaveOverrides}
            >
              Save overrides
            </Button>
          </div>
        </Card>

        <Card className="p-4">
          <Typography variant="h5">Attached sources</Typography>
          <div className="mt-4 flex flex-col gap-3">
            {visibleSourceRows.length ? (
              visibleSourceRows.map((source) => (
                <div
                  key={source.id}
                  className="rounded border border-sage-200 p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip
                      color={source.isAnchor ? 'success' : 'default'}
                      label={source.isAnchor ? 'anchor' : source.role}
                    />
                    <Typography variant="subtitle2">
                      {source.formResponseId}
                    </Typography>
                  </div>
                  <Typography className="mt-2" variant="body2">
                    {[source.respondentFirstName, source.respondentLastName]
                      .filter(Boolean)
                      .join(' ') || 'Unnamed respondent'}
                  </Typography>
                  <Typography variant="body2">
                    {source.respondentEmail ?? 'No email mapped'}
                  </Typography>
                  <Typography variant="body2">
                    {source.formType} · {source.status}
                  </Typography>
                </div>
              ))
            ) : (
              <Typography variant="body2">No sources attached.</Typography>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PulseReportDetailPage;
